import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import * as api from "./api";
import { AuthContext, type AuthContextValue } from "./AuthContext";
import {
  clearCachedUser,
  clearToken,
  loadCachedUser,
  loadToken,
  saveCachedUser,
  saveToken,
} from "./storage";
import type { AuthUser } from "./types";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => loadCachedUser());
  const [loading, setLoading] = useState<boolean>(() => !!loadToken());

  const applySession = useCallback((next: AuthUser, token: string) => {
    saveToken(token);
    saveCachedUser(next);
    setUser(next);
  }, []);

  const clearLocal = useCallback(() => {
    clearToken();
    clearCachedUser();
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    if (!loadToken()) {
      clearLocal();
      setLoading(false);
      return;
    }
    try {
      const { user: fresh } = await api.fetchSession();
      saveCachedUser(fresh);
      setUser(fresh);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401) {
        clearLocal();
      }
      // network errors: keep cached user; user can retry later
    } finally {
      setLoading(false);
    }
  }, [clearLocal]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      async login(input) {
        const result = await api.login(input);
        applySession(result.user, result.token);
        return result.user;
      },
      async signup(input) {
        const result = await api.signup(input);
        applySession(result.user, result.token);
        return { user: result.user, recoveryCode: result.recoveryCode };
      },
      async logout() {
        try {
          await api.logout();
        } catch {
          /* logging out locally is fine even if the server call failed */
        }
        clearLocal();
      },
      refresh,
      applySession,
      async updateProfile(input) {
        const { user: updated } = await api.updateProfile(input);
        saveCachedUser(updated);
        setUser(updated);
        return updated;
      },
      async changePassword(input) {
        await api.changePassword(input);
      },
      async rotateRecovery() {
        const { recoveryCode } = await api.rotateRecovery();
        return recoveryCode;
      },
      async redeemRecovery(input) {
        const result = await api.redeemRecovery(input);
        applySession(result.user, result.token);
        return result.user;
      },
    }),
    [user, loading, applySession, clearLocal, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
